/**
 * Finalize Order Card Component
 * Handles order finalization, completion, status changes, and related modals.
 * 
 * P0 QC Workflow: All completion decisions are backend-enforced.
 * Frontend calls /validate-completion and renders backend response.
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../App';
import { isTaxProduct } from '../../utils/orderHelpers';
import { getRoleDashboardPath } from '../../utils/roleDashboard';
import MailAwayModal from '../MailAwayModal';
import { CycleAckModal } from '../MultiJurisdictionMailAway';
import OrderSummaryPopup from '../OrderSummaryPopup';
import { 
  ReopenOrderModal, 
  ReplaceFinalPdfModal, 
  ValidationBlockModal 
} from '../modals';
// Extracted components
import { StatusOptionsGrid, StatusReasonField } from './finalize';
import FaxDeliverySection from './FaxDeliverySection';
import TaxEmailRequestsSection from './TaxEmailRequestsSection';
import { showTaxEmailAutomationToasts } from '../../utils/taxEmailToasts';
import EmailConfirmModal from './EmailConfirmModal';
// P0 QC Workflow - Validation hook
import { useCompletionValidation, formatValidationErrors, canCompleteOrder } from '../../hooks/useCompletionValidation';
import { validateParcelsForFax, validateParcelsForEmail } from '../../utils/parcelValidation';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * FinalizeOrderCard Component
 * 
 * Props:
 * - orderData: The order object being finalized
 * - certificateData: Tax certificate data (optional)
 * - onStatusChange: Callback when order status changes
 * - isReadOnly: Boolean to disable editing
 */
const FinalizeOrderCard = ({ orderData, certificateData = null, onStatusChange, isReadOnly = false, externalMailAwayTrigger = false, onMailAwayClose }) => {
  const navigate = useNavigate();
  
  // DEBUG: Trace where this component is being mounted from
  console.log('🔍 FinalizeOrderCard MOUNTED - Order:', orderData?.order_number, 'Stack:', new Error().stack?.split('\n').slice(1, 5).join(' <- '));
  
  console.log('🔍 FinalizeOrderCard - Certificate data available:', certificateData ? 'Yes' : 'No');
  if (certificateData) {
    console.log('   MLC Charges from certificate:', certificateData.mlc_charges);
    console.log('   🏛️ Jurisdiction Type from certificate:', certificateData.jurisdiction_type);
  }
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showReplacePdfModal, setShowReplacePdfModal] = useState(false);
  const [showValidationBlockModal, setShowValidationBlockModal] = useState(false);
  const [validationBlockFields, setValidationBlockFields] = useState([]);
  const [reopenReason, setReopenReason] = useState('');
  const [replacementReason, setReplacementReason] = useState('');
  const [isReopeningOrder, setIsReopeningOrder] = useState(false);
  const [isReplacingPdf, setIsReplacingPdf] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [hasShownValidationToast, setHasShownValidationToast] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedChipIds, setSelectedChipIds] = useState([]);
  // TAX-EMAIL-APN-GATE-001
  const [hasEmailChip, setHasEmailChip] = useState(false);
  const handleChipIdsChange = (ids, meta) => { setSelectedChipIds(ids); setHasEmailChip(!!meta?.hasEmailAutomation); };
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [showMailAwayModal, setShowMailAwayModal] = useState(false);
  // Soft duplicate-cycle ack modal (Feb 27, 2026)
  // Opens when /mailaway/send returns 409 MAILAWAY_CYCLE_ACK_REQUIRED.
  // Stores the original mailawayDetails so we can retry the same send
  // with cycle_acknowledged: true after the operator confirms.
  const [cycleAckModalState, setCycleAckModalState] = useState({
    open: false,
    mailawayDetails: null,
    pendingCycles: [],
  });
  const [showMailAwayDuplicateModal, setShowMailAwayDuplicateModal] = useState(false);
  const [duplicateMailAwayJurisdiction, setDuplicateMailAwayJurisdiction] = useState('');
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [showExternalPortalValidationModal, setShowExternalPortalValidationModal] = useState(false);
  const [externalPortalMissingFields, setExternalPortalMissingFields] = useState([]);
  const [isExternalPortalClient, setIsExternalPortalClient] = useState(false);
  const [clientCodeConfig, setClientCodeConfig] = useState(null); // Store full client code config for Mail-Away
  const [hasTaxCertErrors, setHasTaxCertErrors] = useState(true); // Track Tax Certificate validation state
  const [hasAnyFiles, setHasAnyFiles] = useState(false); // Track if order has any files/attachments
  const [orderFiles, setOrderFiles] = useState([]); // Store order files for evidence validation
  const [evidenceValidation, setEvidenceValidation] = useState({ isValid: true, errors: [] }); // Source evidence validation
  // DELIVERY-UX (May 4, 2026) — confirmation modal state for the
  // initial Tax Cert send. Resolved recipient is fetched from
  // `/api/orders/{id}/delivery/info` so the user sees the current
  // client-config email and can correct it inline before completing.
  const [showCompleteEmailModal, setShowCompleteEmailModal] = useState(false);
  const [completeEmailResolving, setCompleteEmailResolving] = useState(false);
  const [completeEmailResolved, setCompleteEmailResolved] = useState('');
  const [completeEmailKnown, setCompleteEmailKnown] = useState('');
  const { user } = useAuth();
  
  // External trigger for Mail-Away modal (from WorkflowActionsSection)
  useEffect(() => {
    if (externalMailAwayTrigger) {
      console.log('📬 [FINALIZE] External Mail-Away trigger received, opening modal');
      setShowMailAwayModal(true);
    }
  }, [externalMailAwayTrigger]);
  
  // Notify parent when Mail-Away modal closes
  const handleMailAwayModalClose = () => {
    setShowMailAwayModal(false);
    if (onMailAwayClose) {
      onMailAwayClose();
    }
  };
  
  // P0 QC Workflow - Backend validation hook
  const { 
    validationResult: backendValidation, 
    isValidating: isBackendValidating, 
    validateForCompletion,
    clearValidation 
  } = useCompletionValidation();
  
  // P0 QC Workflow - Track if order is in QC review status
  const isQCReview = orderData.status === 'qc_review';
  
  // Check if order is already complete
  const isOrderComplete = orderData.status === 'complete';
  
  // Monitor autosave state to block finalization during saves
  useEffect(() => {
    const checkAutoSaveState = () => {
      // Check if autosave is in progress by looking for saving indicators
      const savingElements = document.querySelectorAll('[data-saving="true"]');
      setIsAutoSaving(savingElements.length > 0);
    };
    
    // Poll for autosave state
    const interval = setInterval(checkAutoSaveState, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Monitor form changes to detect unsaved changes
  useEffect(() => {
    const checkUnsavedChanges = () => {
      // Check for any unsaved form changes in the document
      const unsavedInputs = document.querySelectorAll('input[data-changed="true"], textarea[data-changed="true"], select[data-changed="true"]');
      setHasUnsavedChanges(unsavedInputs.length > 0);
    };
    
    const interval = setInterval(checkUnsavedChanges, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Check if this is an external portal client and get client config for Mail-Away
  useEffect(() => {
    const checkExternalPortalClient = async () => {
      try {
        const clientCode = orderData.client_code;
        const clientCodeId = orderData.client_code_id;
        
        if (!clientCode && !clientCodeId) {
          setIsExternalPortalClient(false);
          setClientCodeConfig(null);
          return;
        }
        
        // Fetch client codes and check if this client uses external portal
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/client-codes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const clientCodes = response.data || [];
        const matchingClient = clientCodes.find(
          cc => cc.id === clientCodeId || cc.code === clientCode
        );
        
        if (matchingClient) {
          // Store the full client config for Mail-Away shipping auto-population
          setClientCodeConfig(matchingClient);
          console.log('[CLIENT CONFIG] Loaded client code config:', matchingClient.code, {
            default_shipping_method: matchingClient.default_shipping_method,
            default_shipping_fee: matchingClient.default_shipping_fee
          });
          
          if ((matchingClient.tax_delivery_method || matchingClient.delivery_method) === 'external_portal') {
            console.log('[EXTERNAL PORTAL] Client uses external tax portal:', matchingClient.code);
            setIsExternalPortalClient(true);
            setHasTaxCertErrors(false); // Enable Complete button for external portal clients
          } else {
            setIsExternalPortalClient(false);
          }
        } else {
          setIsExternalPortalClient(false);
          setClientCodeConfig(null);
        }
      } catch (error) {
        console.error('Error checking external portal client:', error);
        setIsExternalPortalClient(false);
        setClientCodeConfig(null);
      }
    };
    
    checkExternalPortalClient();
  }, [orderData.client_code, orderData.client_code_id]);
  
  // Check if order has any files/attachments (required for completion)
  // Excludes clarification documents - they should not satisfy the completion requirement
  // Also validates source evidence requirements per jurisdiction
  useEffect(() => {
    const checkOrderFiles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/orders/${orderData.id}/files`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Check if there are any active files
        // Handle both array and object response formats: {files: [], total: 0} or []
        const filesData = response.data;
        const files = Array.isArray(filesData) ? filesData : (filesData?.files || []);
        
        // Store all files for evidence validation
        setOrderFiles(files);
        
        // Filter out clarification-related documents from completion requirement
        // These document types should NOT count as supporting documents for completion:
        // - clarification_response: Files uploaded in response to clarification requests
        // - clarification_request: Original clarification request documents
        // - clarification: General clarification correspondence
        const EXCLUDED_FILE_CATEGORIES = [
          'clarification_response',
          'clarification_request', 
          'clarification',
          'clarification_received'
        ];
        
        const activeFiles = files.filter(f => {
          // Must be active
          if (f.is_active === false) return false;
          
          // Exclude clarification documents from completion count
          const fileCategory = (f.file_category || f.file_type || '').toLowerCase();
          if (EXCLUDED_FILE_CATEGORIES.includes(fileCategory)) {
            console.log('[FILE CHECK] Excluding clarification file from completion count:', f.filename, fileCategory);
            return false;
          }
          
          return true;
        });
        
        setHasAnyFiles(activeFiles.length > 0);
        
        console.log('[FILE CHECK] Order has', activeFiles.length, 'completion-eligible files (excluding clarification docs)');
      } catch (error) {
        console.error('Error checking order files:', error);
        // On error, assume no files (safe default)
        setHasAnyFiles(false);
        setOrderFiles([]);
      }
    };
    
    // Check immediately
    checkOrderFiles();
    
    // Then poll every 2 seconds to catch new uploads and deletions
    // This ensures Evidence Status Indicator updates promptly when files are added/removed
    const interval = setInterval(checkOrderFiles, 2000);
    return () => clearInterval(interval);
  }, [orderData.id]);
  
  // Monitor Tax Certificate validation errors in real-time
  useEffect(() => {
    const checkTaxCertValidity = async () => {
      // If external portal client, skip validation and enable Complete button
      if (isExternalPortalClient) {
        console.log('[COMPLETE BUTTON DEBUG] External portal client - skipping validation');
        setHasTaxCertErrors(false);
        return;
      }
      
      try {
        console.log('[COMPLETE BUTTON DEBUG] === VALIDATION CHECK STARTING ===');
        
        // PRIORITY: Use props-passed certificateData if available (from TaxCertificatePanel form state)
        // This is more reliable than fetching from API, especially when auto-save has failed
        let cert = null;
        let usedSource = '';
        
        if (certificateData && (certificateData.full_address || certificateData.parcels?.length > 0)) {
          // Use the props-passed certificate data (form state from TaxCertificatePanel)
          console.log('[COMPLETE BUTTON DEBUG] Using props-passed certificateData (form state)');
          cert = certificateData;
          usedSource = 'props';
        } else {
          // Fallback: Fetch from API only if props data is empty
          console.log('[COMPLETE BUTTON DEBUG] Fetching from API (props data empty)');
          const response = await axios.get(`${API}/orders/${orderData.id}/tax-cert`);
          const apiData = response.data;
          
          // API returns data nested in 'certificate' property
          cert = apiData.certificate || apiData;
          usedSource = 'api';
          
          // If API also returns null/empty, check if we have props data as final fallback
          if ((!cert || !cert.full_address) && certificateData) {
            console.log('[COMPLETE BUTTON DEBUG] API returned null, falling back to props data');
            cert = certificateData;
            usedSource = 'props-fallback';
          }
        }
        
        console.log('[COMPLETE BUTTON DEBUG] Data source used:', usedSource);
        console.log('[COMPLETE BUTTON DEBUG] Tax cert data:', cert);
        console.log('[COMPLETE BUTTON DEBUG] Parcels:', cert?.parcels);
        console.log('[COMPLETE BUTTON DEBUG] Parcel 0:', cert?.parcels?.[0]);
        console.log('[COMPLETE BUTTON DEBUG] Parcel 0 parcel_apn:', cert?.parcels?.[0]?.parcel_apn);
        
        // P0 FIX: If parcels array is missing but flat parcel data exists, construct parcels array
        // This handles the case where TaxCertificatePanel passes flat data without parcels array
        let parcelsToValidate = cert?.parcels || [];
        if (parcelsToValidate.length === 0 && cert?.parcel_apn) {
          console.log('[COMPLETE BUTTON DEBUG] Constructing parcels from flat data');
          // Construct a parcel from flat data (legacy/single-parcel format)
          parcelsToValidate = [{
            parcel_apn: cert.parcel_apn,
            tax_collected: cert.tax_collected,
            jurisdiction_type: cert.jurisdiction_type,
            make_checks_payable_to: cert.make_checks_payable_to,
            current_tax_summary: cert.current_tax_summary || []
          }];
        }
        console.log('[COMPLETE BUTTON DEBUG] Parcels to validate:', parcelsToValidate);
          
          let hasErrors = false;
          const errors = [];
          
          // Check Property/Borrower fields
          if (!cert.full_address?.trim()) errors.push('Missing full_address');
          if (!cert.city?.trim()) errors.push('Missing city');
          if (!cert.state?.trim()) errors.push('Missing state');
          if (!cert.zip?.trim()) errors.push('Missing zip');
          if (!cert.county?.trim()) errors.push('Missing county');
          if (!cert.borrower_owner_name?.trim()) errors.push('Missing borrower_owner_name');
          
          if (errors.length > 0) {
            console.log('[COMPLETE BUTTON DEBUG] Property/Borrower validation failed:', errors);
            hasErrors = true;
          }
          
          // Check parcels - use parcelsToValidate which may have been constructed from flat data
          if (parcelsToValidate.length === 0) {
            console.log('[COMPLETE BUTTON DEBUG] No parcels found');
            errors.push('No parcels');
            hasErrors = true;
          } else {
            for (let pi = 0; pi < parcelsToValidate.length; pi++) {
              const parcel = parcelsToValidate[pi];
              const parcelErrors = [];
              
              // P0 FIX: Extract jurisdiction_type from current_tax_summary OR jurisdictions[] if not at parcel level
              // In the new UI, jurisdiction_type is stored per-row in current_tax_summary OR in jurisdictions[] array
              const taxSummary = parcel.current_tax_summary || [];
              const firstRow = taxSummary[0] || {};
              const jurisdictionsArray = cert?.jurisdictions || [];
              const firstJurisdiction = jurisdictionsArray[0] || {};
              
              const effectiveJurisdictionType = parcel.jurisdiction_type 
                || firstRow.jurisdiction_type
                || firstJurisdiction.jurisdiction_type;
              const effectiveMakeChecksPayableTo = parcel.make_checks_payable_to 
                || firstJurisdiction.make_checks_payable_to
                || firstRow.make_checks_payable_to;
              
              // Required parcel fields
              if (!parcel.parcel_apn?.trim()) parcelErrors.push('Missing parcel_apn');
              if (!parcel.tax_collected) parcelErrors.push('Missing tax_collected');
              if (!effectiveJurisdictionType) parcelErrors.push('Missing jurisdiction_type');
              if (!effectiveMakeChecksPayableTo?.trim()) parcelErrors.push('Missing make_checks_payable_to');
              
              if (parcelErrors.length > 0) {
                console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1} validation failed:`, parcelErrors);
                errors.push(`Parcel ${pi + 1}: ${parcelErrors.join(', ')}`);
                hasErrors = true;
                break;
              }
              
              // Check Current Tax Summary
              // P0 FIX: Handle both string and number types for tax values
              // P0 DEBUG: Added detailed field-by-field logging to identify exact failure point
              const hasValue = (val) => {
                if (val === null || val === undefined) return false;
                if (typeof val === 'string') return val.trim() !== '';
                if (typeof val === 'number') return true;
                return Boolean(val);
              };
              
              // P0 DEBUG: Log each row's field validation status
              let hasValidTaxSummary = false;
              if (parcel.current_tax_summary && parcel.current_tax_summary.length > 0) {
                for (let rowIdx = 0; rowIdx < parcel.current_tax_summary.length; rowIdx++) {
                  const row = parcel.current_tax_summary[rowIdx];
                  
                  // P0 DEBUG: Log ALL field values and their validation status
                  const fieldChecks = {
                    jurisdiction: { value: row.jurisdiction, valid: hasValue(row.jurisdiction) },
                    tax_year: { value: row.tax_year, valid: hasValue(row.tax_year) },
                    base_tax: { value: row.base_tax, valid: hasValue(row.base_tax) },
                    amounts_fees: { value: row.amounts_fees, valid: hasValue(row.amounts_fees) },
                    mailing_issue_date: { value: row.mailing_issue_date, valid: hasValue(row.mailing_issue_date) },
                    mailing_date: { value: row.mailing_date, valid: hasValue(row.mailing_date) },
                    issue_date: { value: row.issue_date, valid: hasValue(row.issue_date) },
                    installment: { value: row.installment, valid: hasValue(row.installment) },
                    pay_status: { value: row.pay_status, valid: hasValue(row.pay_status) }
                  };
                  
                  console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1}, Row ${rowIdx + 1} - FIELD BY FIELD VALIDATION:`);
                  console.log(`[COMPLETE BUTTON DEBUG]   jurisdiction: "${row.jurisdiction}" -> ${fieldChecks.jurisdiction.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   tax_year: "${row.tax_year}" -> ${fieldChecks.tax_year.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   base_tax: "${row.base_tax}" -> ${fieldChecks.base_tax.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   amounts_fees: "${row.amounts_fees}" -> ${fieldChecks.amounts_fees.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   mailing_issue_date: "${row.mailing_issue_date}" -> ${fieldChecks.mailing_issue_date.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   mailing_date: "${row.mailing_date}" -> ${fieldChecks.mailing_date.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   issue_date: "${row.issue_date}" -> ${fieldChecks.issue_date.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   installment: "${row.installment}" -> ${fieldChecks.installment.valid ? '✓ PASS' : '✗ FAIL'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   pay_status: "${row.pay_status}" -> ${fieldChecks.pay_status.valid ? '✓ PASS' : '✗ FAIL'}`);
                  
                  // Check if this row is valid (all required fields present)
                  const hasMailingDate = fieldChecks.mailing_issue_date.valid || fieldChecks.mailing_date.valid || fieldChecks.issue_date.valid;
                  const rowIsValid = fieldChecks.jurisdiction.valid && 
                    fieldChecks.tax_year.valid && 
                    fieldChecks.base_tax.valid &&
                    fieldChecks.amounts_fees.valid &&
                    hasMailingDate &&
                    fieldChecks.installment.valid &&
                    fieldChecks.pay_status.valid;
                  
                  console.log(`[COMPLETE BUTTON DEBUG]   => ROW ${rowIdx + 1} OVERALL: ${rowIsValid ? '✓ VALID' : '✗ INVALID'}`);
                  console.log(`[COMPLETE BUTTON DEBUG]   Raw row object:`, row);
                  
                  if (rowIsValid) {
                    hasValidTaxSummary = true;
                    break; // Found at least one valid row
                  }
                }
              }
              
              console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1} current_tax_summary:`, parcel.current_tax_summary);
              console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1} hasValidTaxSummary:`, hasValidTaxSummary);
              
              if (!hasValidTaxSummary) {
                console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1} has no valid tax summary rows`);
                console.log(`[COMPLETE BUTTON DEBUG] Tax summary:`, parcel.current_tax_summary);
                errors.push(`Parcel ${pi + 1}: No valid tax summary rows`);
                hasErrors = true;
                break;
              }
              
              // Validate each row in Current Tax Summary for required fields
              if (parcel.current_tax_summary) {
                // P0 FIX: Helper to check if value exists (handles both string and number types)
                const hasValue = (val) => {
                  if (val === null || val === undefined) return false;
                  if (typeof val === 'string') return val.trim() !== '';
                  if (typeof val === 'number') return true;
                  return Boolean(val);
                };
                
                for (let ri = 0; ri < parcel.current_tax_summary.length; ri++) {
                  const row = parcel.current_tax_summary[ri];
                  const rowErrors = [];
                  
                  // Skip empty rows (no jurisdiction = empty row)
                  if (!hasValue(row.jurisdiction)) continue;
                  
                  // Check required fields for non-empty rows
                  if (!hasValue(row.mailing_issue_date) && !hasValue(row.mailing_date) && !hasValue(row.issue_date)) {
                    rowErrors.push('Missing Mailing/Issue Date');
                  }
                  if (!hasValue(row.installment)) rowErrors.push('Missing Installment');
                  if (!hasValue(row.pay_status)) rowErrors.push('Missing Pay Status');
                  
                  // Check Tax Amount (amounts_fees) - REQUIRED
                  if (!hasValue(row.amounts_fees)) {
                    rowErrors.push('Missing Tax Amount');
                  } else {
                    const taxAmount = typeof row.amounts_fees === 'number' 
                      ? row.amounts_fees 
                      : parseFloat(row.amounts_fees);
                    if (taxAmount <= 0) {
                      rowErrors.push('Tax Amount must be > 0');
                    }
                  }
                  
                  if (rowErrors.length > 0) {
                    console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1}, Row ${ri + 1} validation failed:`, rowErrors);
                    console.log(`[COMPLETE BUTTON DEBUG] Row data:`, row);
                    errors.push(`Parcel ${pi + 1}, Row ${ri + 1}: ${rowErrors.join(', ')}`);
                    hasErrors = true;
                    break;
                  }
                  
                  // Check conditional fields based on pay_status
                  if (row.pay_status === 'Paid' || row.pay_status === 'Partially Paid') {
                    if (!hasValue(row.paid_date)) {
                      console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1}, Row ${ri + 1}: Missing paid_date for status ${row.pay_status}`);
                      errors.push(`Parcel ${pi + 1}, Row ${ri + 1}: Missing Paid Date`);
                      hasErrors = true;
                      break;
                    }
                  }
                  
                  // Delinquency Date is required for "Due" and "Partially Paid" statuses
                  // NOT required for "Delinquent" or "Partially DLQ" (field is disabled in those cases)
                  if (row.pay_status === 'Due' || row.pay_status === 'Partially Paid') {
                    if (!row.dlq_date?.trim()) {
                      console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1}, Row ${ri + 1}: Missing dlq_date for status ${row.pay_status}`);
                      errors.push(`Parcel ${pi + 1}, Row ${ri + 1}: Missing dlq_date for ${row.pay_status}`);
                      hasErrors = true;
                      break;
                    }
                  }
                  
                  if (row.pay_status === 'Partially Paid') {
                    if (!row.balance_amount?.trim() || parseFloat(row.balance_amount) <= 0) {
                      console.log(`[COMPLETE BUTTON DEBUG] Parcel ${pi + 1}, Row ${ri + 1}: Missing/invalid balance_amount for Partially Paid`);
                      errors.push(`Parcel ${pi + 1}, Row ${ri + 1}: Missing/invalid balance_amount`);
                      hasErrors = true;
                      break;
                    }
                  }
                }
              }
            }
          }
          
          if (errors.length > 0) {
            console.log('[COMPLETE BUTTON DEBUG] All validation errors:', errors);
          }
          
          console.log('[COMPLETE BUTTON DEBUG] Tax Cert Validation Result:', {
            hasErrors,
            parcelsCount: parcelsToValidate.length,
            cert: cert
          });
          
          setHasTaxCertErrors(hasErrors);
      } catch (error) {
        console.error('[COMPLETE BUTTON DEBUG] Error checking tax cert validity:', error);
        // On error, assume there are errors (safe default)
        setHasTaxCertErrors(true);
      }
    };
    
    // Check immediately
    checkTaxCertValidity();
    
    // Then poll every 2 seconds
    const interval = setInterval(checkTaxCertValidity, 2000);
    return () => clearInterval(interval);
  }, [orderData.id, isExternalPortalClient, certificateData]);
  
  // ============================================================================
  // SOURCE EVIDENCE VALIDATION
  // Validates that required evidence files are attached based on Source selection
  // per jurisdiction row in current_tax_summary OR for External Portal (single source)
  // ============================================================================
  useEffect(() => {
    const validateSourceEvidence = () => {
      const errors = [];
      
      // Get file categories from uploaded files
      const fileCategories = orderFiles.filter(f => f.is_active !== false)
        .map(f => (f.file_category || f.file_type || '').toLowerCase());
      
      const hasCallRecording = fileCategories.includes('call_recording');
      const hasSourceDocument = fileCategories.includes('source_document');
      const hasEmailEvidence = fileCategories.includes('email_evidence');
      const hasFaxEvidence = fileCategories.includes('fax_evidence');
      const hasMailEvidence = fileCategories.includes('mail_evidence');
      
      // Helper function to validate a single source
      const validateSource = (source, jurisdictionLabel, rowIndex) => {
        const normalizedSource = (source || '').toLowerCase();
        
        // Check if source is selected
        if (!normalizedSource) {
          errors.push({
            type: 'missing_source',
            row: rowIndex,
            jurisdiction: jurisdictionLabel,
            message: `${jurisdictionLabel}: Please select the information source before completing the file.`
          });
          return;
        }
        
        // Validate evidence based on source
        switch (normalizedSource) {
          case 'calling':
            if (!hasCallRecording) {
              errors.push({
                type: 'missing_evidence',
                row: rowIndex,
                jurisdiction: jurisdictionLabel,
                source: 'Calling',
                requiredEvidence: 'Call Recording',
                message: `${jurisdictionLabel}: Calling selected as the source. Please attach the call recording under the Files tab.`
              });
            }
            break;
            
          case 'online':
            if (!hasSourceDocument) {
              errors.push({
                type: 'missing_evidence',
                row: rowIndex,
                jurisdiction: jurisdictionLabel,
                source: 'Online',
                requiredEvidence: 'Source Document',
                message: `${jurisdictionLabel}: Online selected as the source. Please attach supporting documentation under the Files tab.`
              });
            }
            break;
            
          case 'calling_online':
            const missing = [];
            if (!hasCallRecording) missing.push('call recording');
            if (!hasSourceDocument) missing.push('source document');
            if (missing.length > 0) {
              errors.push({
                type: 'missing_evidence',
                row: rowIndex,
                jurisdiction: jurisdictionLabel,
                source: 'Calling/Online',
                requiredEvidence: missing.join(' and '),
                message: `${jurisdictionLabel}: Calling/Online verification requires both a call recording and supporting documentation. Missing: ${missing.join(', ')}.`
              });
            }
            break;
            
          case 'email':
            if (!hasEmailEvidence) {
              errors.push({
                type: 'missing_evidence',
                row: rowIndex,
                jurisdiction: jurisdictionLabel,
                source: 'Email',
                requiredEvidence: 'Email Evidence',
                message: `${jurisdictionLabel}: Email selected as the source. Please attach email evidence under the Files tab.`
              });
            }
            break;
            
          case 'fax':
            if (!hasFaxEvidence) {
              errors.push({
                type: 'missing_evidence',
                row: rowIndex,
                jurisdiction: jurisdictionLabel,
                source: 'Fax',
                requiredEvidence: 'Fax Evidence',
                message: `${jurisdictionLabel}: Fax selected as the source. Please attach fax evidence under the Files tab.`
              });
            }
            break;
            
          case 'mail':
            if (!hasMailEvidence) {
              errors.push({
                type: 'missing_evidence',
                row: rowIndex,
                jurisdiction: jurisdictionLabel,
                source: 'Mail',
                requiredEvidence: 'Mail Evidence',
                message: `${jurisdictionLabel}: Mail selected as the source. Please attach mail evidence under the Files tab.`
              });
            }
            break;
            
          case 'other':
            // 'Other' doesn't require specific evidence
            break;
            
          default:
            break;
        }
      };
      
      // Get tax summary rows from certificateData or orderData
      let taxSummaryRows = [];
      
      if (certificateData?.current_tax_summary?.length > 0) {
        taxSummaryRows = certificateData.current_tax_summary;
      } else if (certificateData?.parcels?.[0]?.current_tax_summary?.length > 0) {
        taxSummaryRows = certificateData.parcels[0].current_tax_summary;
      }
      
      // CASE 1: External Portal Client - Single jurisdiction with source at root level
      if (isExternalPortalClient) {
        // For External Portal, source is stored at certificateData.source (not in current_tax_summary)
        const jurisdictionLabel = certificateData?.jurisdiction_type 
          ? `${certificateData.jurisdiction_type.charAt(0).toUpperCase()}${certificateData.jurisdiction_type.slice(1)} Jurisdiction`
          : 'Jurisdiction';
        
        validateSource(certificateData?.source, jurisdictionLabel, 1);
      }
      // CASE 2: Internal Fulfillment - Check source at parcel level or certificateData level
      // Note: In the legacy data model, source is stored at parcel level (parcel.source),
      // NOT in individual current_tax_summary rows (row.source doesn't exist)
      else if (taxSummaryRows.length > 0) {
        // Get source from certificateData or first parcel - source is shared across all rows
        const effectiveSource = certificateData?.source 
          || certificateData?.parcels?.[0]?.source 
          || orderData?.parcels?.[0]?.source;
        
        // If we have a source at parcel/certificate level, validate it once
        if (effectiveSource) {
          validateSource(effectiveSource, 'Tax Certificate', 1);
        } else {
          // No source found - this is a validation error
          errors.push({
            rowIndex: 1,
            source: null,
            requiredEvidence: null,
            message: 'Please select the information source before completing the file.'
          });
        }
      }
      // CASE 3: No jurisdiction rows - skip validation
      else {
        setEvidenceValidation({ isValid: true, errors: [] });
        return;
      }
      
      console.log('[EVIDENCE VALIDATION] Results:', { isValid: errors.length === 0, errorCount: errors.length, errors });
      setEvidenceValidation({ isValid: errors.length === 0, errors });
    };
    
    validateSourceEvidence();
  }, [certificateData, orderFiles, isExternalPortalClient]);
  
  const clearValidationErrors = () => {
    setValidationErrors({});
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!selectedStatus) {
      errors.status = 'Please select a status';
      return errors;
    }
    
    if ((selectedStatus === 'pending' || selectedStatus === 'clarification') && (!reason || reason.trim().length < 10)) {
      errors.reason = 'Reason must be at least 10 characters long';
    }
    
    return errors;
  };
  
  const isFormValid = () => {
    const errors = validateForm();
    return Object.keys(errors).length === 0;
  };
  
  const isSubmitDisabled = () => {
    return !isFormValid() || isSubmitting || hasUnsavedChanges || isAutoSaving || isReadOnly;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitDisabled()) return;
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Handle Mail-Away status - open modal, backend handles create/update/blocked (Case C support)
    if (selectedStatus === 'mailaway') {
      // Open MailAwayModal - backend will decide if this is a create, update, or block
      // This supports Case C: Adding new parcel after initial Mail-Away send
      setShowMailAwayModal(true);
      return;
    }
    
    // ── Parcel/APN validation: block fax send if any parcel missing APN ──
    const reasonLower = (reason || '').toLowerCase();
    const isFaxChipSelected = (selectedChipIds && selectedChipIds.includes('fax_sent')) ||
      (reasonLower.includes('fax') && (reasonLower.includes('sent') || reasonLower.includes('request')));
    if (isFaxChipSelected) {
      const parcelCheck = validateParcelsForFax(orderData, certificateData);
      if (!parcelCheck.valid) {
        toast.error(parcelCheck.message, { duration: 5000 });
        setValidationErrors({ reason: parcelCheck.message });
        return;
      }
    }
    
    // ── TAX-EMAIL-APN-GATE-001: Email chip requires a Parcel/APN on EVERY parcel (backend enforces too) ──
    if (selectedStatus === 'pending' && hasEmailChip) {
      const emailCheck = validateParcelsForEmail(orderData, certificateData?.parcels || null);
      if (!emailCheck.valid) {
        toast.error(emailCheck.message, { duration: 6000 });
        setValidationErrors({ reason: emailCheck.message });
        return;
      }
    }
    
    setIsSubmitting(true);
    clearValidationErrors();
    
    try {
      const token = localStorage.getItem('token');
      
      const requestData = {
        status: selectedStatus,
        source: 'ui_finalize'
      };
      
      if ((selectedStatus === 'pending' || selectedStatus === 'clarification') && reason.trim()) {
        requestData.reason = reason.trim();
      }
      
      // Include structured chip IDs for automation triggers (e.g., fax_sent)
      if (selectedChipIds && selectedChipIds.length > 0) {
        requestData.selected_chip_ids = selectedChipIds;
      }
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestData)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Show appropriate success message based on status
        let successMessage = '';
        const completedStatus = selectedStatus; // Save status before resetting
        
        if (selectedStatus === 'pending') {
          successMessage = 'Pending status and reason saved. Order remains Pending.';
        } else if (selectedStatus === 'clarification') {
          successMessage = 'Clarification status and reason saved. Order remains Pending.';
        } else if (selectedStatus === 'complete') {
          successMessage = 'Order completed successfully! Redirecting to dashboard...';
        } else {
          successMessage = `Order status updated to ${selectedStatus}`;
        }
        
        toast.success(successMessage, { duration: 2000 });
        
        // Show fax automation feedback if applicable
        if (result.fax_automation_status) {
          const faxStatus = result.fax_automation_status;
          if (faxStatus === 'sent') {
            toast.success('Fax request auto-sent successfully', { duration: 3000 });
          } else if (faxStatus === 'send_failed') {
            toast.error(`Fax automation failed: ${result.fax_error || 'Unknown error'}`, { duration: 5000 });
          } else if (faxStatus === 'blocked_by_role') {
            toast.warning('Fax automation skipped — your role does not have fax permissions', { duration: 4000 });
          } else if (faxStatus === 'duplicate_blocked') {
            toast.info('Fax already sent for this order — duplicate skipped', { duration: 3000 });
          } else if (faxStatus === 'client_fax_disabled') {
            toast.info('Fax automation skipped — fax not enabled for this client', { duration: 3000 });
          } else if (faxStatus === 'client_automation_disabled') {
            toast.info('Fax automation skipped — automation not enabled for this client', { duration: 3000 });
          } else if (faxStatus === 'no_delivery_email') {
            toast.warning('Fax automation skipped — no fax delivery email configured for this client', { duration: 4000 });
          } else if (faxStatus === 'error') {
            toast.error('Fax automation encountered an error — check order activity log', { duration: 5000 });
          }
        }

        // TAX-PENDING-EMAIL-001: Tax Collector email automation feedback
        showTaxEmailAutomationToasts(result.email_automation);
        
        // Reset form state
        setSelectedStatus(null);
        setReason('');
        
        // Track telemetry
        if (window.gtag) {
          window.gtag('event', 'finalize_order_success', {
            event_category: 'order_management',
            event_label: completedStatus,
            order_id: orderData.id
          });
        }
        
        // GLOBAL RULE: Redirect to dashboard after ANY successful status update
        // Applies to: Admin, Team Leader, Tax Processor, Title Processor
        // Excluded: Order Desk (they cannot change order statuses)
        // This prevents stale UI, duplicate actions, and ensures fresh dashboard data
        const rolesWithDashboardRedirect = [
          'admin', 
          'team_leader', 
          'tax_search_team_leader', 
          'title_search_team_leader',
          'processor',
          'tax_processor', 
          'title_processor',
          'search_processor',
          'title_search_processor'
        ];
        
        const userRole = user?.role?.toLowerCase() || '';
        const shouldRedirect = rolesWithDashboardRedirect.includes(userRole);
        
        // P1 FIX: Only redirect if on fulfillment page
        const currentPath = window.location.pathname;
        const isOnFulfillmentPage = currentPath.includes('/orders/') && currentPath.includes('/fulfillment');
        
        if (shouldRedirect && isOnFulfillmentPage) {
          console.log(`[FINALIZE] Status changed to ${completedStatus}. Redirecting to dashboard in 1.5 seconds...`);
          setTimeout(() => {
            console.log('[FINALIZE] Executing redirect to dashboard NOW');
            navigate(getRoleDashboardPath(user), { replace: true });
          }, 1500);
        } else {
          // For roles without redirect OR when not on fulfillment page, just refresh data
          console.log(`[FINALIZE] Status changed to ${completedStatus}. NO redirect (not on fulfillment page or role excluded).`);
          if (onStatusChange) {
            onStatusChange(result.order);
          }
        }
        
      } else {
        // Handle error responses
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 400) {
          // Check for duplicate Mail-Away error from backend
          const errorCode = response.headers.get('X-Error-Code');
          if (errorCode === 'duplicate_mailaway_request' || errorCode === 'already_in_mailaway') {
            // Extract jurisdiction name from error message
            const match = errorData.detail?.match(/Jurisdiction:\s*([^)]+)/);
            const jurisdictionName = match ? match[1].trim() : 'this order';
            setDuplicateMailAwayJurisdiction(jurisdictionName);
            setShowMailAwayDuplicateModal(true);
            setIsSubmitting(false);
            return;
          }
          
          // Check for external portal validation error
          if (errorCode === 'external_portal_validation_failed') {
            const missingFields = response.headers.get('X-Missing-Fields');
            setExternalPortalMissingFields(missingFields ? missingFields.split(', ') : []);
            setShowExternalPortalValidationModal(true);
            setIsSubmitting(false);
            return;
          }
          
          // Validation errors
          if (errorCode === 'validation_failed') {
            const validationDetails = response.headers.get('X-Validation-Details');
            setValidationErrors({
              validation: validationDetails || 'Tax certificate validation failed. Please complete all required fields.'
            });
          } else if (errorCode === 'reason_required') {
            setValidationErrors({ reason: 'A reason is required for this status' });
          } else if (errorData.detail?.code === 'parcel_apn_required' || String(errorData.detail?.code || '').startsWith('tax_email_') || String(errorData.detail?.code || '').startsWith('email_jurisdiction')) {
            // TAX-EMAIL-APN-GATE-001
            toast.error(errorData.detail.message, { duration: 6000 });
            setValidationErrors({ reason: errorData.detail.message });
          } else {
            setValidationErrors({
              general: typeof errorData.detail === 'string' ? errorData.detail : (errorData.detail?.message || 'Validation failed. Please check your inputs.')
            });
          }
        } else if (response.status === 403) {
          // Parse the error detail from backend for more specific message
          const errorDetail = errorData.detail || "You don't have permission to perform this action";
          setValidationErrors({
            general: errorDetail
          });
        } else {
          setValidationErrors({
            general: errorData.detail || 'An error occurred while finalizing the order'
          });
        }
        
        // Track telemetry for failures
        if (window.gtag) {
          window.gtag('event', 'finalize_order_failure', {
            event_category: 'order_management',
            event_label: `${response.status}_${selectedStatus}`,
            order_id: orderData.id
          });
        }
      }
      
    } catch (error) {
      console.error('Error finalizing order:', error);
      setValidationErrors({
        general: 'Network error occurred. Please check your connection and try again.'
      });
      
      // Track telemetry for network errors
      if (window.gtag) {
        window.gtag('event', 'finalize_order_network_error', {
          event_category: 'order_management',
          order_id: orderData.id
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMailAwaySubmit = async (mailawayDetails, cycleAcknowledged = false, resumeJurisdictionIds = null) => {
    // DEBUG: Trace what triggered this function
    console.log('🚨 [FINALIZE] handleMailAwaySubmit CALLED - Stack:', new Error().stack?.split('\n').slice(1, 5).join(' <- '));
    console.log('🚨 [FINALIZE] mailawayDetails:', mailawayDetails);
    
    // P1 SAFETY GUARD: Only allow Mail-Away submission from Order Details/Fulfillment page
    // This prevents accidental triggers from dashboard/popup refreshes
    const currentPath = window.location.pathname;
    const isOnOrderDetailsPage = currentPath.includes('/orders/') && currentPath.includes('/fulfillment');
    
    if (!isOnOrderDetailsPage) {
      console.warn('⛔ [FINALIZE] handleMailAwaySubmit blocked - not on order details page. Current path:', currentPath);
      return; // Block the submit
    }
    
    setIsSubmitting(true);
    clearValidationErrors();
    
    try {
      const token = localStorage.getItem('token');
      
      // Use the jurisdiction IDs from the modal's formData (already scoped if target was set)
      const jurisdictionsToSend = (mailawayDetails.jurisdiction_shipping || []).map(js => js.jurisdiction_id);
      
      if (jurisdictionsToSend.length === 0) {
        toast.error('No jurisdictions selected for Mail-Away');
        setShowMailAwayModal(false);
        setIsSubmitting(false);
        return;
      }
      
      // Per-jurisdiction shipping and SASE (no global SASE - each jurisdiction has its own)
      const requestData = {
        jurisdiction_ids: jurisdictionsToSend,
        // Legacy global fields for backward compatibility
        tax_office_fee: mailawayDetails.tax_office_fee,
        notes: mailawayDetails.notes || null,
        // Per-jurisdiction shipping data (includes sase_required per jurisdiction)
        jurisdiction_shipping: mailawayDetails.jurisdiction_shipping || null,
        // Legacy global shipping (only used if per-jurisdiction not provided)
        shipment_charges: mailawayDetails.shipment_charges,
        shipping_method: mailawayDetails.shipping_method || null,
        // Soft duplicate-cycle ack (Feb 27, 2026) — true on retry
        // after operator confirms via the CycleAckModal.
        cycle_acknowledged: cycleAcknowledged,
        // Feb 2026 (Option C) — Resume after Clarification:
        // `order_previous_status` helps the backend's resume gate detect
        // a Clarification round-trip; `resume_jurisdiction_ids` lets
        // the operator confirm Resume via the green CycleAckModal.
        order_previous_status:
          orderData?.previous_status ||
          orderData?.status ||
          null,
        resume_jurisdiction_ids: resumeJurisdictionIds || null,
      };
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/mailaway/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestData)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        // Show accurate toast based on action types
        const createdCount = result.results?.filter(r => r.success && r.action === 'created').length || 0;
        const updatedCount = result.results?.filter(r => r.success && r.action === 'updated').length || 0;
        const resumedCount = result.results?.filter(r => r.success && r.resumed).length || 0;
        const blockedCount = result.results?.filter(r => !r.success).length || 0;

        // Feb 2026 (Option C) — when every result is blocked, route based
        // on the structured suggested_action so we open the right modal
        // (Resume / Cycle 2) instead of toasting a misleading message.
        if (createdCount === 0 && updatedCount === 0 && resumedCount === 0 && blockedCount > 0) {
          const firstBlocked = result.results?.find(r => !r.success) || {};
          const suggested = firstBlocked.suggested_action;

          if (suggested === 'resume') {
            setShowMailAwayModal(false);
            setCycleAckModalState({
              open: true,
              mailawayDetails,
              pendingCycles: [{
                jurisdiction_id: firstBlocked.jurisdiction_id,
                jurisdiction_name: firstBlocked.jurisdiction_name,
                cycle_index: 2,
                trigger: 'post_clarification',
                previous_request_id: firstBlocked.previous_request_id,
                previous_status: firstBlocked.previous_request_status,
                resume_eligible: true,
                resume_reason: 'match_after_clarification',
                resume_diff_fields: [],
              }],
            });
            setIsSubmitting(false);
            return;
          }
          if (suggested === 'cycle2') {
            setShowMailAwayModal(false);
            setCycleAckModalState({
              open: true,
              mailawayDetails,
              pendingCycles: [{
                jurisdiction_id: firstBlocked.jurisdiction_id,
                jurisdiction_name: firstBlocked.jurisdiction_name,
                cycle_index: 2,
                trigger: 'manual_resend',
                previous_request_id: firstBlocked.previous_request_id,
                previous_status: firstBlocked.previous_request_status,
                resume_eligible: false,
                resume_reason: 'shipment_signature_changed',
                resume_diff_fields: firstBlocked.resume_diff_fields || [],
              }],
            });
            setIsSubmitting(false);
            return;
          }
          if (suggested === 'resend') {
            toast.info('Mail-Away already confirmed sent', {
              description: firstBlocked.error,
              duration: 7000,
            });
          } else if (suggested === 'confirm_sent') {
            toast.warning('Confirm Sent required', {
              description: firstBlocked.error,
              duration: 7000,
            });
          } else {
            toast.info(firstBlocked.error || 'No changes made');
          }
          setShowMailAwayModal(false);
          setIsSubmitting(false);
          return;
        }

        if (resumedCount > 0) {
          toast.success('Mail-Away resumed', {
            description: 'Resumed existing Mail-Away — no new charge.',
            duration: 4000,
          });
        } else if (createdCount > 0 && updatedCount > 0) {
          toast.success(`Mail-Away: ${createdCount} created, ${updatedCount} updated`);
        } else if (updatedCount > 0) {
          toast.success(`Mail-Away request${updatedCount > 1 ? 's' : ''} updated with new parcels`);
        } else if (createdCount > 0) {
          toast.success(`Mail-Away request${createdCount > 1 ? 's' : ''} created successfully`);
        } else if (blockedCount > 0) {
          const firstError = result.results?.find(r => !r.success)?.error || 'No changes made';
          toast.info(firstError);
        }
        
        // Reset form state
        setSelectedStatus(null);
        setReason('');
        setShowMailAwayModal(false);
        
        // Track telemetry
        if (window.gtag) {
          window.gtag('event', 'finalize_order_success', {
            event_category: 'order_management',
            event_label: 'mailaway',
            order_id: orderData.id
          });
        }
        
        // GLOBAL RULE: Redirect to dashboard after ANY successful status update
        // Applies to: Admin, Team Leader, Tax Processor, Title Processor
        const rolesWithDashboardRedirect = [
          'admin', 
          'team_leader', 
          'tax_search_team_leader', 
          'title_search_team_leader',
          'processor',
          'tax_processor', 
          'title_processor',
          'search_processor',
          'title_search_processor'
        ];
        
        const userRole = user?.role?.toLowerCase() || '';
        const shouldRedirect = rolesWithDashboardRedirect.includes(userRole);
        
        // P1 FIX: Double-check we're on the fulfillment page before redirecting
        // This prevents redirect when FinalizeOrderCard is triggered from Order Summary popup
        const currentPath = window.location.pathname;
        const isOnFulfillmentPage = currentPath.includes('/orders/') && currentPath.includes('/fulfillment');
        
        if (shouldRedirect && isOnFulfillmentPage) {
          console.log('[FINALIZE] Mail-Away status set. Redirecting to dashboard in 1.5 seconds...');
          setTimeout(() => {
            console.log('[FINALIZE] Executing redirect to dashboard NOW');
            navigate(getRoleDashboardPath(user), { replace: true });
          }, 1500);
        } else {
          // For roles without redirect OR when not on fulfillment page, just refresh data
          console.log('[FINALIZE] Mail-Away status set. NO redirect (not on fulfillment page or role excluded).');
          if (onStatusChange) {
            onStatusChange(result.order);
          }
        }
        
      } else {
        // Handle error responses
        const errorData = await response.json().catch(() => ({}));
        
        // Close the modal on error so user can see the error message
        setShowMailAwayModal(false);
        
        let errorMessage = '';
        
        // Soft duplicate-cycle gate (Feb 27, 2026) — backend returns
        // 409 with structured detail when a jurisdiction would land
        // on cycle ≥ 2. Open the ack modal instead of toasting an
        // error. Falls through to standard error handling otherwise.
        const detail = errorData?.detail;
        if (
          response.status === 409 &&
          detail &&
          typeof detail === 'object' &&
          detail.error === 'MAILAWAY_CYCLE_ACK_REQUIRED'
        ) {
          setCycleAckModalState({
            open: true,
            mailawayDetails,
            pendingCycles: detail.pending_cycles || [],
          });
          setIsSubmitting(false);
          return;
        }

        if (response.status === 400) {
          // Validation errors
          const errorCode = response.headers.get('X-Error-Code');
          if (errorCode === 'mailaway_details_required') {
            errorMessage = 'Mail-Away details are required';
          } else if (errorCode === 'invalid_tax_office_fee') {
            errorMessage = 'Tax office fee must be a positive number';
          } else {
            // Use the backend's detailed error message
            errorMessage = (typeof detail === 'string' ? detail : detail?.message) || 'Validation failed. Please check your inputs.';
          }
        } else if (response.status === 403) {
          // Parse the error detail from backend for more specific message
          errorMessage = (typeof detail === 'string' ? detail : detail?.message) || "You don't have permission to perform this action";
        } else {
          errorMessage = (typeof detail === 'string' ? detail : detail?.message) || 'An error occurred while creating the mail-away request';
        }
        
        // Show error as a toast for better visibility
        toast.error(errorMessage, {
          duration: 8000,  // Show longer for important validation messages
          description: 'Please correct the issue and try again.'
        });
        
        // Also set validation errors for UI display
        setValidationErrors({
          general: errorMessage
        });
        
        // Track telemetry for failures
        if (window.gtag) {
          window.gtag('event', 'finalize_order_failure', {
            event_category: 'order_management',
            event_label: `${response.status}_mailaway`,
            order_id: orderData.id
          });
        }
      }
      
    } catch (error) {
      console.error('Error creating mail-away request:', error);
      setValidationErrors({
        general: 'Network error occurred. Please check your connection and try again.'
      });
      
      // Track telemetry for network errors
      if (window.gtag) {
        window.gtag('event', 'finalize_order_network_error', {
          event_category: 'order_management',
          order_id: orderData.id
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // DELIVERY-UX (May 4, 2026) — Step 1 of the Complete flow:
  // resolve the recipient email from client config and open the
  // EmailConfirmModal. The user verifies / corrects, then we call
  // handleComplete(overrideEmail) which actually fires /complete.
  // External-portal clients skip the modal — there is no email to send.
  const openCompleteEmailModal = async () => {
    // External-portal orders don't email — go straight to complete.
    if (isExternalPortalClient) {
      handleComplete();
      return;
    }
    setCompleteEmailResolving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API}/orders/${orderData.id}/delivery/info`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let resolved = '';
      let known = '';
      if (res.ok) {
        const info = await res.json();
        resolved = info.delivery_recipient || '';
        known = info.client_config_delivery_email || resolved;
      }
      setCompleteEmailResolved(resolved);
      setCompleteEmailKnown(known);
      setShowCompleteEmailModal(true);
    } catch (err) {
      console.error('[COMPLETE] Failed to resolve delivery email:', err);
      // Fallback — open modal with empty recipient; user can type in.
      setCompleteEmailResolved('');
      setCompleteEmailKnown('');
      setShowCompleteEmailModal(true);
    } finally {
      setCompleteEmailResolving(false);
    }
  };

  const handleComplete = async (deliveryRecipientOverride = null) => {
    setIsCompleting(true);
    clearValidationErrors();
    
    try {
      const token = localStorage.getItem('token');
      
      const requestData = {
        completion_notes: completionNotes.trim() || null
      };
      if (deliveryRecipientOverride && typeof deliveryRecipientOverride === 'string') {
        requestData.delivery_recipient_override = deliveryRecipientOverride.trim();
      }
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestData)
        }
      );
      
      console.log('[COMPLETE] Response status:', response.status, 'ok:', response.ok);
      
      if (response.ok) {
        const result = await response.json();
        console.log('[COMPLETE] Response data:', result);
        
        // Reset completion state
        setCompletionNotes('');
        
        // P0 QC Workflow: Check if order was routed to QC review
        if (result.delivery_status === 'qc_review') {
          // Order routed to QC review (user has requires_qc=true)
          toast.success(
            'Order submitted for QC review. A QC Specialist will review and complete the order.',
            { duration: 4000, icon: <Clock className="h-5 w-5 text-blue-500" /> }
          );
          
          // Track telemetry
          if (window.gtag) {
            window.gtag('event', 'order_qc_submitted', {
              event_category: 'order_management',
              event_label: 'qc_review_routing',
              order_id: orderData.id
            });
          }
          
          // Refresh to show QC status
          setTimeout(() => {
            navigate(getRoleDashboardPath(user), { replace: true });
          }, 2000);
          
        } else {
          // Direct completion success
          toast.success('Order completed successfully! Redirecting to dashboard...', { duration: 2000 });
          
          // Track telemetry
          if (window.gtag) {
            window.gtag('event', 'order_complete_success', {
              event_category: 'order_management',
              event_label: result.external_portal ? 'external_portal_complete' : 'tax_search_complete',
              order_id: orderData.id
            });
          }
          
          // Redirect to dashboard immediately after showing toast
          console.log('[COMPLETE] Redirecting to dashboard in 1.5 seconds...');
          setTimeout(() => {
            console.log('[COMPLETE] Executing redirect to dashboard NOW');
            navigate(getRoleDashboardPath(user), { replace: true });
          }, 1500);
        }
        
      } else {
        // Handle error responses
        const errorData = await response.json().catch(() => ({}));
        
        if (response.status === 400) {
          setValidationErrors({
            general: errorData.detail || 'Order cannot be completed at this time. Please ensure tax certificate is filled out.'
          });
        } else if (response.status === 403) {
          setValidationErrors({
            general: "You don't have permission to complete this order"
          });
        } else {
          const _d = errorData.detail;
          setValidationErrors({
            general: (_d && typeof _d === 'object' ? _d.message : _d) || 'An error occurred while completing the order'
          });
        }
        
        // Track telemetry for failures
        if (window.gtag) {
          window.gtag('event', 'order_complete_failure', {
            event_category: 'order_management',
            event_label: `${response.status}_complete`,
            order_id: orderData.id
          });
        }
      }
      
    } catch (error) {
      console.error('Error completing order:', error);
      setValidationErrors({
        general: 'Network error occurred. Please check your connection and try again.'
      });
      
      // Track telemetry for network errors
      if (window.gtag) {
        window.gtag('event', 'order_complete_network_error', {
          event_category: 'order_management',
          order_id: orderData.id
        });
      }
    } finally {
      setIsCompleting(false);
    }
  };
  
  const handleReopenOrder = async () => {
    if (!reopenReason.trim()) {
      setValidationErrors({ reopen: 'Reopen reason is required' });
      return;
    }
    
    setIsReopeningOrder(true);
    clearValidationErrors();
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/reopen`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ reopen_reason: reopenReason.trim() })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        setShowReopenModal(false);
        setReopenReason('');
        
        toast.success('Order reopened successfully! Status set to Open, unassigned, and ready for re-assignment. Redirecting to dashboard...', {
          duration: 3000
        });
        
        if (onStatusChange) {
          onStatusChange(result.order);
        }
        
        // Redirect to dashboard after 2 seconds
        console.log('[REOPEN] Scheduling redirect to dashboard in 2 seconds...');
        setTimeout(() => {
          console.log('[REOPEN] Executing redirect to dashboard now');
          // Feb 27, 2026 — role-aware (processors → /dashboard/processor-orders)
          navigate(getRoleDashboardPath(user), { replace: true });
        }, 2000);
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        setValidationErrors({
          reopen: errorData.detail || 'Failed to reopen order'
        });
      }
      
    } catch (error) {
      console.error('Error reopening order:', error);
      setValidationErrors({
        reopen: 'Network error occurred. Please try again.'
      });
    } finally {
      setIsReopeningOrder(false);
    }
  };
  
  const handleReplaceFinalPdf = async () => {
    setIsReplacingPdf(true);
    clearValidationErrors();
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/final-pdf/replace`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ 
            replacement_reason: replacementReason.trim() || null 
          })
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        
        setShowReplacePdfModal(false);
        setReplacementReason('');
        
        toast.success(`Final PDF replaced successfully: ${result.new_pdf_filename}`);
        
        if (onStatusChange) {
          // Refresh order data to get updated final_pdf_id
          window.location.reload();
        }
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        setValidationErrors({
          replace: errorData.detail || 'Failed to replace final PDF'
        });
      }
      
    } catch (error) {
      console.error('Error replacing final PDF:', error);
      setValidationErrors({
        replace: 'Network error occurred. Please try again.'
      });
    } finally {
      setIsReplacingPdf(false);
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'text-green-700 bg-green-100 border-green-300';
      case 'pending': return 'text-amber-700 bg-amber-100 border-amber-300';
      case 'clarification': return 'text-purple-700 bg-purple-100 border-purple-300';
      case 'mailaway': return 'text-cyan-700 bg-cyan-100 border-cyan-300';
      default: return 'text-gray-700 bg-gray-100 border-gray-300';
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'complete': return 'Complete';
      case 'pending': return 'Pending';
      case 'clarification': return 'Clarification';
      case 'qc_review': return 'QC Review';
      case 'mail_away': return 'Mail-Away';
      case 'mail_away_received': return 'Mail-Away Received';
      case 'clarification_received': return 'Clarification Received';
      default: return status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || status;
    }
  };
  
  if (isOrderComplete) {
    const isAdmin = user?.role === 'admin';
    
    return (
      <div className="bg-white rounded-lg shadow" data-testid="finalize-order-complete">
        <div className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Order Status</h2>
            
            {/* Admin Actions */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowReopenModal(true)}
                  variant="outline"
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                  data-testid="reopen-order-btn"
                >
                  Reopen for Edit
                </Button>
                <Button
                  onClick={() => setShowReplacePdfModal(true)}
                  variant="outline" 
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="replace-pdf-btn"
                >
                  Replace Final PDF
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor('complete')}`}>
              Complete
            </span>
            <span className="text-gray-600">This order has been finalized and locked.</span>
          </div>
          
          {/* Order locked banner for processors */}
          {!isAdmin && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-yellow-800 text-sm">
                <strong>Order completed.</strong> Editing requires Team Lead/Admin approval.
              </p>
            </div>
          )}
          
          {/* Final PDF Information */}
          {orderData.final_pdf_id && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-800 text-sm">
                <strong>Final PDF generated:</strong> Tax_Cert_{orderData.order_number}_{new Date().toISOString().slice(0,16).replace(/[-:]/g, '').replace('T', '_')}.pdf
              </p>
              <p className="text-green-700 text-xs mt-1">
                The final PDF is available in the Files tab for download and preview.
              </p>
            </div>
          )}
        </div>
        
        {/* Reopen Order Modal */}
        <ReopenOrderModal
          isOpen={showReopenModal}
          onClose={() => setShowReopenModal(false)}
          onReopen={handleReopenOrder}
          isReopening={isReopeningOrder}
          reopenReason={reopenReason}
          onReasonChange={setReopenReason}
          validationErrors={validationErrors}
          orderData={orderData}
        />
        
        {/* Replace Final PDF Modal */}
        <ReplaceFinalPdfModal
          isOpen={showReplacePdfModal}
          onClose={() => setShowReplacePdfModal(false)}
          onReplace={handleReplaceFinalPdf}
          isReplacing={isReplacingPdf}
          replacementReason={replacementReason}
          onReasonChange={setReplacementReason}
          validationErrors={validationErrors}
          orderData={orderData}
        />
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-lg shadow" data-testid="finalize-order-card">
      <div className="px-6 py-4 border-b">
        <div className="bg-gradient-to-r from-[#4c2aff] to-[#784bff] text-white font-bold text-lg px-4 py-3 rounded-lg shadow-md">
          Finalize Order
        </div>
      </div>
      <div className="p-6">
        
        {/* Unsaved Changes Warning */}
        {(hasUnsavedChanges || isAutoSaving) && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-yellow-700 text-sm">
                {isAutoSaving ? 'Saving changes...' : 'You have unsaved changes. Please wait for auto-save to complete.'}
              </span>
            </div>
          </div>
        )}
        
        {/* Validation Errors */}
        {Object.keys(validationErrors).length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-red-700 text-sm space-y-1">
              {Object.entries(validationErrors).map(([key, error]) => (
                <div key={key}>{error}</div>
              ))}
            </div>
          </div>
        )}
        
        {/* Complete Tax Search / Complete Title Search Section */}
        <div className="bg-[#eef2ff] border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-blue-900 mb-2">
                {isTaxProduct(orderData) ? 'Complete Tax Search' : 'Complete Title Search'}
              </h3>
              {/* P0 QC Workflow: Show QC status if order is in QC review */}
              {isQCReview && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
                  <Clock className="w-4 h-4" />
                  <span>Order is pending QC review. A QC Specialist will complete this order.</span>
                </div>
              )}
              {/* Attachment requirement warning */}
              {!hasAnyFiles && !isQCReview && (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>Please upload supporting document(s) before completing this order.</span>
                </div>
              )}
              {/* File requirement satisfied - show ready indicator */}
              {!isQCReview && hasAnyFiles && (
                <p className="text-xs text-green-600 italic">
                  <CheckCircle className="w-3 h-3 inline mr-1" />
                  Supporting document(s) uploaded. Ready to complete.
                </p>
              )}
            </div>
            <div>
              <Button
                onClick={async () => {
                  // ATTACHMENT REQUIREMENT: Check if order has any files before attempting completion
                  // This provides immediate feedback to users without hitting the backend
                  if (!hasAnyFiles) {
                    toast.error('Please upload the supporting document(s) before completing this order.', { duration: 5000 });
                    return;
                  }
                  
                  // P0 QC Workflow: Run backend validation first (all decisions are backend-enforced)
                  const validationResult = await validateForCompletion(orderData.id);
                  
                  if (!canCompleteOrder(validationResult)) {
                    // Show backend validation errors
                    const formattedErrors = formatValidationErrors(validationResult.errors || []);
                    
                    if (formattedErrors.length > 0) {
                      setValidationBlockFields(formattedErrors);
                      setShowValidationBlockModal(true);
                      return;
                    }
                    
                    // If no specific errors, show generic message
                    toast.error('Order cannot be completed. Please check all required fields.', { duration: 5000 });
                    return;
                  }
                  
                  // Backend validation passed - continue with completion
                  // (Backend will still enforce attachment requirement)
                  
                  // PHASE 1: Validate Tax Certificate before allowing completion
                  // Skip validation for External Portal clients - they don't need full tax cert
                  if (!isExternalPortalClient) {
                    try {
                      const token = localStorage.getItem('token');
                      
                      // Fetch tax certificate data to validate
                      const response = await fetch(`${API}/orders/${orderData.id}/tax-cert`, {
                        headers: {
                          'Authorization': `Bearer ${token}`,
                        },
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        const cert = data.certificate;
                        
                        // Validate Tax Certificate completeness
                        const missingFields = [];
                        
                        // Property/Borrower validation
                        if (!cert.full_address?.trim()) missingFields.push('Full Address');
                        if (!cert.city?.trim()) missingFields.push('City');
                        if (!cert.state?.trim()) missingFields.push('State');
                        if (!cert.zip?.trim()) missingFields.push('ZIP');
                        if (!cert.county?.trim()) missingFields.push('County');
                        if (!cert.borrower_owner_name?.trim()) missingFields.push('Borrower Name');
                        
                        // P0 FIX: Use same parcel logic as button enable/disable validation
                        // Get parcels from response OR construct from flat data if missing
                        let parcelsToCheck = cert.parcels || [];
                        if (parcelsToCheck.length === 0 && cert.parcel_apn) {
                          // Construct parcel from flat data (single-parcel format)
                          parcelsToCheck = [{
                            parcel_apn: cert.parcel_apn,
                            tax_collected: cert.tax_collected,
                            jurisdiction_type: cert.jurisdiction_type,
                            make_checks_payable_to: cert.make_checks_payable_to,
                            current_tax_summary: cert.current_tax_summary || []
                          }];
                        }
                        
                        // Validate parcels
                        if (parcelsToCheck.length === 0) {
                          missingFields.push('At least one parcel is required');
                        } else {
                          parcelsToCheck.forEach((parcel, index) => {
                            const parcelNum = index + 1;
                            if (!parcel.parcel_apn?.trim()) missingFields.push(`Parcel ${parcelNum}: APN`);
                            if (!parcel.tax_collected) missingFields.push(`Parcel ${parcelNum}: Tax Collected`);
                            if (!parcel.jurisdiction_type) missingFields.push(`Parcel ${parcelNum}: Jurisdiction Type`);
                            if (!parcel.make_checks_payable_to?.trim()) missingFields.push(`Parcel ${parcelNum}: Make Checks Payable To`);
                            
                            // Validate Current Tax Summary
                            const hasValidTaxSummary = parcel.current_tax_summary?.some(row => 
                              row.jurisdiction?.trim() && 
                              row.tax_year?.trim() && 
                              row.base_tax?.trim()
                            );
                            if (!hasValidTaxSummary) {
                              missingFields.push(`Parcel ${parcelNum}: Current Tax Summary (Jurisdiction, Tax Year, Base Tax required)`);
                            }
                          });
                        }
                        
                        if (missingFields.length > 0) {
                          setValidationBlockFields(missingFields);
                          setShowValidationBlockModal(true);
                          return;
                        }
                      }
                    } catch (error) {
                      console.error('Error validating tax certificate:', error);
                    }
                    
                    // Check for assessed values validation errors
                    const hasAssessedErrors = validationErrors.assessed_land_value || 
                                            validationErrors.assessed_building_value || 
                                            validationErrors.assessed_total_value;
                    
                    if (hasAssessedErrors) {
                      // Show blocking modal with field list
                      const errorFields = [];
                      if (validationErrors.assessed_land_value) errorFields.push('Land Value');
                      if (validationErrors.assessed_building_value) errorFields.push('Building Value');
                      if (validationErrors.assessed_total_value) errorFields.push('Total Assessed Value');
                      
                      setValidationBlockFields(errorFields);
                      setShowValidationBlockModal(true);
                      return;
                    }
                  }
                  
                  // Clear any previous validation errors and complete order directly
                  // No modal needed - completion notes are optional and can be added via Activity log
                  clearValidationErrors();
                  setCompletionNotes(''); // Ensure notes are empty
                  // DELIVERY-UX (May 4, 2026): gate Complete with email
                  // confirmation modal. External-portal orders skip the
                  // modal (no email to send).
                  openCompleteEmailModal();
                }}
                disabled={hasUnsavedChanges || isAutoSaving || isReadOnly || isCompleting || completeEmailResolving || isBackendValidating || isQCReview || (!isExternalPortalClient && hasTaxCertErrors) || !evidenceValidation.isValid}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  isQCReview
                    ? "Order is pending QC review"
                    : isBackendValidating
                      ? "Validating order..."
                      : !evidenceValidation.isValid
                        ? `Source evidence required: ${evidenceValidation.errors[0]?.message || 'Please upload required evidence files'}`
                        : isExternalPortalClient
                          ? "External Portal Client - Complete order without PDF generation"
                          : hasTaxCertErrors
                            ? "Complete will be enabled once all required Tax Certificate fields are valid"
                            : hasUnsavedChanges 
                              ? "Please save all changes before completing" 
                              : isAutoSaving 
                                ? "Please wait for autosave to finish" 
                                : "Click to complete the order"
                }
                data-testid="complete-order-btn"
              >
                {isCompleting || isBackendValidating ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {isBackendValidating ? 'Validating...' : 'Completing...'}
                  </div>
                ) : (
                  isExternalPortalClient ? 'Complete (External Portal)' : 'Complete'
                )}
              </Button>
              
              {/* ========== PHASE 3: EVIDENCE STATUS INDICATOR ========== */}
              {/* Shows verification status near the Complete button */}
              {evidenceValidation.isValid && certificateData?.current_tax_summary?.some(row => row.source) && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md" data-testid="evidence-verified">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Evidence Verified ✓</span>
                  </div>
                </div>
              )}
              
              {/* Evidence Validation Errors */}
              {!evidenceValidation.isValid && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="evidence-validation-errors">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Evidence Required</p>
                      <ul className="mt-1 text-xs text-amber-700 space-y-1">
                        {evidenceValidation.errors.map((error, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span className="text-amber-500">•</span>
                            <span>{error.message}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2 text-xs text-amber-600">
                        Please upload the required evidence files in the <strong>Files tab</strong> before completing.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* General Validation Error Display */}
          {validationErrors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="validation-error-banner">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800">{validationErrors.general}</p>
              </div>
              <button 
                type="button"
                onClick={() => setValidationErrors({})}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}
          
          {/* Status Selection - Using extracted component */}
          {/* Product-based: Mail-Away only available for Tax products, hidden for Search products */}
          {/* Jurisdiction requirement: Mail-Away requires a valid Jurisdiction Type to be selected */}
          {/* Parcel/APN requirement: Mail-Away also requires a valid Parcel/APN to be entered */}
          <StatusOptionsGrid
            selectedStatus={selectedStatus}
            onStatusChange={setSelectedStatus}
            isReadOnly={isReadOnly}
            clearValidationErrors={clearValidationErrors}
            userRole={user?.role}
            productType={orderData?.client_product}
            hasJurisdiction={(() => {
              // Check if jurisdiction_type is set
              // jurisdiction_type can be at top level OR in parcels (depending on data structure)
              
              // Check top-level jurisdiction_type first
              if (certificateData?.jurisdiction_type && certificateData.jurisdiction_type.trim() !== '') {
                return true;
              }
              
              // Also check parcels for jurisdiction_type (backward compatibility)
              const parcels = certificateData?.parcels || [];
              if (parcels.some(parcel => parcel?.jurisdiction_type && parcel.jurisdiction_type.trim() !== '')) {
                return true;
              }
              
              return false;
            })()}
            hasParcelAPN={(() => {
              // Check if Parcel/APN is entered
              // parcel_apns can be at top level OR in current_tax_summary rows
              
              // Check top-level parcel_apns first
              if (certificateData?.parcel_apns) {
                const apn = Array.isArray(certificateData.parcel_apns) 
                  ? certificateData.parcel_apns[0] 
                  : certificateData.parcel_apns;
                if (apn && String(apn).trim() !== '') {
                  return true;
                }
              }
              
              // Also check current_tax_summary rows for parcel_apn
              const taxSummary = certificateData?.current_tax_summary || [];
              if (taxSummary.some(row => row?.parcel_apn && String(row.parcel_apn).trim() !== '')) {
                return true;
              }
              
              // Also check orderData for parcel information
              if (orderData?.parcel_apns) {
                const apn = Array.isArray(orderData.parcel_apns) 
                  ? orderData.parcel_apns[0] 
                  : orderData.parcel_apns;
                if (apn && String(apn).trim() !== '') {
                  return true;
                }
              }
              
              return false;
            })()}
          />
          
          {/* Reason Field - Using extracted component with template support */}
          <StatusReasonField
            selectedStatus={selectedStatus}
            reason={reason}
            onReasonChange={setReason}
            onChipIdsChange={handleChipIdsChange}
            liveParcels={certificateData?.parcels || null}
            userRole={user?.role}
            validationError={validationErrors.reason}
            isReadOnly={isReadOnly}
            clearValidationErrors={clearValidationErrors}
            orderData={orderData}
            jurisdictionData={(() => {
              // Multi-jurisdiction: prefer first jurisdiction from jurisdictions[] array
              const jurs = certificateData?.jurisdictions || [];
              const firstJur = jurs[0] || {};
              const county = firstJur.jurisdiction_name
                || certificateData?.county
                || orderData?.property_county
                || '';
              return {
                county,
                jurisdiction_name: county,
                tax_collector_phone: firstJur.tax_collector_phone || certificateData?.tax_collector_phone || '',
                phone_number: firstJur.tax_collector_phone || certificateData?.tax_collector_phone || '',
                tax_collector_email: firstJur.tax_collector_email || certificateData?.tax_collector_email || '',
                tax_collector_website: firstJur.tax_collector_website || certificateData?.tax_collector_website || '',
                tax_collector_fax: firstJur.tax_collector_fax || certificateData?.tax_collector_fax || (() => {
                  // Deep fallback: check parcel jurisdictions for fax
                  const parcels = certificateData?.parcels || [];
                  for (const p of parcels) {
                    for (const pj of (p.jurisdictions || [])) {
                      if (pj.tax_collector_fax) return pj.tax_collector_fax;
                    }
                  }
                  return '';
                })(),
                property_address: certificateData?.full_address || orderData?.property_address || '',
                client_name: orderData?.client_name || orderData?.client_code || '',
              };
            })()}
          />
          
          {/* Submit Button */}
          <div className="pt-4 border-t">
            <Button
              type="submit"
              disabled={isSubmitDisabled()}
              className={`w-full sm:w-auto px-6 py-2 ${
                isSubmitDisabled() 
                  ? 'bg-gray-300 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-md transition-colors`}
              data-testid="submit-order-btn"
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Finalizing...
                </div>
              ) : (
                'Submit Order'
              )}
            </Button>
          </div>
        </form>
      </div>
      
      {/* Fax Delivery Tracking — shown when fax_delivery/fax_deliveries exists */}
      {(orderData?.fax_deliveries?.length > 0 || orderData?.fax_delivery) && (
        <div className="px-4 pb-4">
          <FaxDeliverySection
            orderData={orderData}
            currentUser={user}
            onUpdate={onStatusChange ? () => onStatusChange(orderData) : undefined}
          />
        </div>
      )}

      {/* TAX-PENDING-EMAIL-001 — outbound Tax Collector email requests */}
      {orderData?.tax_email_requests?.length > 0 && (
        <div className="px-4 pb-4">
          <TaxEmailRequestsSection
            orderData={orderData}
            currentUser={user}
            onUpdate={onStatusChange ? () => onStatusChange(orderData) : undefined}
          />
        </div>
      )}
      
      {/* Mail-Away Modal */}
      <MailAwayModal
        isOpen={showMailAwayModal}
        onClose={handleMailAwayModalClose}
        onSubmit={handleMailAwaySubmit}
        orderData={orderData}
        certificateData={certificateData}
        clientCodeConfig={clientCodeConfig}
      />

      {/* Soft duplicate-cycle ack modal (Feb 27, 2026). Opens on 409
          MAILAWAY_CYCLE_ACK_REQUIRED; on confirm, re-submits the
          original mailawayDetails with cycle_acknowledged: true. */}
      {cycleAckModalState.open && (
        <CycleAckModal
          jurisdiction={{
            id: cycleAckModalState.pendingCycles[0]?.jurisdiction_id,
            name: cycleAckModalState.pendingCycles[0]?.jurisdiction_name,
          }}
          notes={cycleAckModalState.mailawayDetails?.notes}
          pendingCycles={cycleAckModalState.pendingCycles}
          loading={isSubmitting}
          onConfirm={async ({ resume } = {}) => {
            // Honor the modal's variant: Resume variant calls
            // onConfirm({ resume: true }) → re-submit with the
            // jurisdiction id in resume_jurisdiction_ids (no new
            // charges). Cycle 2 variant calls onConfirm({ resume:
            // false }) → re-submit with cycle_acknowledged=true.
            const details = cycleAckModalState.mailawayDetails;
            const jurId = cycleAckModalState.pendingCycles[0]?.jurisdiction_id;
            setCycleAckModalState({ open: false, mailawayDetails: null, pendingCycles: [] });
            if (resume && jurId) {
              await handleMailAwaySubmit(details, false, [jurId]);
            } else {
              await handleMailAwaySubmit(details, true);
            }
          }}
          onClose={() =>
            setCycleAckModalState({ open: false, mailawayDetails: null, pendingCycles: [] })
          }
        />
      )}
      
      {/* Mail-Away Duplicate Request Modal - Fancy Popup */}
      {showMailAwayDuplicateModal && (
        <div className="fixed inset-0 z-[100] overflow-y-auto" aria-modal="true" role="dialog">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowMailAwayDuplicateModal(false)}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all">
              {/* Header with Icon */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-2xl px-6 py-5">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Mail-Away Request Already Exists
                    </h3>
                    <p className="text-amber-100 text-sm mt-0.5">
                      Duplicate submission blocked
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5">
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">
                      A Mail-Away request is already pending for this order.
                    </p>
                    <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg">
                      <span className="text-amber-700 font-medium text-sm">Jurisdiction:</span>
                      <span className="text-amber-900 font-semibold text-sm">{duplicateMailAwayJurisdiction}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-blue-700 text-sm">
                      Please manage the existing request from <strong>Order Summary</strong>. You can view status, resend, or mark response as received.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Footer with Buttons */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowMailAwayDuplicateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowMailAwayDuplicateModal(false);
                    // Open Order Summary popup
                    setShowOrderSummary(true);
                  }}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Go to Order Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Complete Order Modal - REMOVED: Completion notes are optional and can be added via Activity log
      <CompleteOrderModal
        isOpen={showCompleteModal}
        onClose={() => {
          clearValidationErrors();
          setShowCompleteModal(false);
        }}
        onComplete={handleComplete}
        isCompleting={isCompleting}
        completionNotes={completionNotes}
        onNotesChange={setCompletionNotes}
        validationErrors={validationErrors}
        orderData={orderData}
      />
      */}
      
      {/* Reopen Order Modal (Team Lead only) */}
      <ReopenOrderModal
        isOpen={showReopenModal}
        onClose={() => setShowReopenModal(false)}
        onReopen={handleReopenOrder}
        isReopening={isReopeningOrder}
        reopenReason={reopenReason}
        onReasonChange={setReopenReason}
        validationErrors={validationErrors}
        orderData={orderData}
      />
      
      {/* Replace Final PDF Modal (Team Lead only) */}
      <ReplaceFinalPdfModal
        isOpen={showReplacePdfModal}
        onClose={() => setShowReplacePdfModal(false)}
        onReplace={handleReplaceFinalPdf}
        isReplacing={isReplacingPdf}
        replacementReason={replacementReason}
        onReasonChange={setReplacementReason}
        validationErrors={validationErrors}
        orderData={orderData}
      />
      
      {/* Validation Block Modal */}
      <ValidationBlockModal
        isOpen={showValidationBlockModal}
        onClose={() => setShowValidationBlockModal(false)}
        errorFields={validationBlockFields}
        onGoToFields={() => {
          // Scroll to Assessed Values section
          const assessedValuesSection = document.getElementById('assessed_land_value');
          if (assessedValuesSection) {
            assessedValuesSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
            assessedValuesSection.focus();
          }
        }}
      />
      
      {/* Order Summary Popup - For viewing Mail-Away status */}
      <OrderSummaryPopup
        isOpen={showOrderSummary}
        onClose={() => setShowOrderSummary(false)}
        orderData={orderData}
        currentUser={user}
        onUpdate={() => {
          // Refresh the page data
          window.location.reload();
        }}
      />
      
      {/* External Tax Portal Validation Modal */}
      {showExternalPortalValidationModal && (
        <div className="fixed inset-0 z-[100] overflow-y-auto" aria-modal="true" role="dialog">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
            onClick={() => setShowExternalPortalValidationModal(false)}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg transform transition-all">
              {/* Header with Icon */}
              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-t-2xl px-6 py-5">
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Missing Routing Information
                    </h3>
                    <p className="text-orange-100 text-sm mt-0.5">
                      External Tax Portal Client
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5">
                <p className="text-gray-700 mb-4">
                  This client uses an external tax portal for tax data entry. However, <strong>Mail-Away requires minimum routing information</strong> to be present in TitleFlow.
                </p>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <h4 className="text-red-800 font-semibold text-sm mb-2 flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Missing Required Fields:
                  </h4>
                  <ul className="text-red-700 text-sm space-y-1">
                    {externalPortalMissingFields.map((field, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                        {field}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-blue-700 text-sm">
                      Please complete the <strong>Jurisdiction section</strong> with the required information before submitting Mail-Away.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Footer with Buttons */}
              <div className="px-6 py-4 bg-gray-50 rounded-b-2xl flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowExternalPortalValidationModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowExternalPortalValidationModal(false);
                    // Scroll to jurisdiction section
                    const jurisdictionSection = document.getElementById('jurisdiction_type') || 
                                               document.getElementById('make_checks_payable_to') ||
                                               document.querySelector('[data-section="jurisdiction"]');
                    if (jurisdictionSection) {
                      jurisdictionSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      jurisdictionSection.focus();
                    }
                  }}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Go to Jurisdiction Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELIVERY-UX (May 4, 2026) — gates the Complete button.
          Resolved recipient is fetched from /delivery/info before
          opening; user can edit inline, see soft typo warnings, and
          confirm. The override (if any) is sent to /complete and
          replaces the auto-resolved recipient for this completion. */}
      <EmailConfirmModal
        open={showCompleteEmailModal}
        onOpenChange={(o) => {
          if (!isCompleting) setShowCompleteEmailModal(o);
        }}
        initialEmail={completeEmailResolved}
        knownEmail={completeEmailKnown || completeEmailResolved}
        title="Confirm Recipient Before Sending"
        description="Completing this order will email the final tax certificate to the address below. Verify the email is correct — this triggers a real send."
        confirmLabel="Confirm & Complete"
        busyLabel="Completing…"
        busy={isCompleting}
        onConfirm={(email) => {
          // Pass the (possibly corrected) email to /complete as an override.
          // If the resolved email and the confirmed email are identical, the
          // override is harmless — backend uses it the same way.
          handleComplete(email);
          setShowCompleteEmailModal(false);
        }}
      />
    </div>
  );
};

export default FinalizeOrderCard;
